// src/screens/EditPlay.tsx
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { PlayCreator } from "./PlayCreator";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import i18n from "../i18n";

type PlayRow = {
  id: string;
  user_id: string;
  name: string;
  data: any;
  tag_ids?: string[];
};

/* ===== i18n mini-hook (safe) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  // stable par langue : sinon `t` change à chaque rendu et relance les useEffect en boucle
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lng]
  );
  return { t };
}


export const EditPlay = () => {
  const { t } = useI18nSafe();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [initial, setInitial] = useState<PlayRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (!id) {
          setErrMsg(t("editplay:errors.missing_id"));
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/login");
          return;
        }

        const { data, error } = await supabase
          .from("plays")
          .select("id, user_id, name, data, tag_ids")
          .eq("id", id)
          .maybeSingle();

        if (error || !data) {
          setErrMsg(t("editplay:errors.not_found"));
          return;
        }

        // Garde-fou côté client (en plus des RLS côté DB)
        if (data.user_id !== user.id) {
          setErrMsg(t("editplay:errors.forbidden"));
          return;
        }

        if (mounted) setInitial(data);
      } catch {
        setErrMsg(t("editplay:errors.load_failed"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [id, navigate, t]);

  if (loading) {
    return (
      <section className="min-h-[50vh] grid place-items-center text-gray-600 dark:text-gray-300">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 rounded-full border-2 border-current border-t-transparent animate-spin" />
          <span>{t("editplay:loading_play")}</span>
        </div>
      </section>
    );
  }

  if (errMsg) {
    return (
      <section className="max-w-2xl mx-auto p-6">
        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          <div className="flex items-start gap-3 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <h2 className="font-semibold mb-1">{t("editplay:unable_to_load")}</h2>
              <p className="text-sm text-gray-700 dark:text-gray-300">{errMsg}</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border
                         bg-white border-gray-300 hover:bg-gray-50
                         dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("common:back")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!initial) {
    return (
      <section className="p-6 text-red-600 dark:text-red-400">
        {t("editplay:no_data")}
      </section>
    );
  }

  return (
    <PlayCreator
      mode="edit"
      initialPhases={initial.data}
      initialName={initial.name}
      initialTags={initial.tag_ids || []}
      playId={initial.id}
    />
  );
};
